import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Button, Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBullseye, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons"
import Paragraph from "../atoms/Paragraph"
import Chip from "../atoms/Chip"
import DashedOutline from "../atoms/DashedOutline"
import { theme } from "../settings/config"
import {
	EXERCISE_MAX_TEXT,
	MAX_EXERCISES,
	OBJECTIVE_MAX_TEXT,
	PHASES,
	PLAYER_STYLES,
	recommendedFor,
} from "../utils/lessonContent.utils"
import { phaseSchemas, phaseSources } from "../utils/notebook.utils"
import FocusAreasPicker from "./FocusAreasPicker"
import SchemaEditor from "./schema/SchemaEditor"
import { CourtIcon, ExerciseSchemaRow } from "./schema/ExerciseSchema"

// L'obiettivo, di una fase o della lezione: una riga sola, sul fondo
// azzurro col bersaglio, perché si distingua da quello che si fa
// `caption` è la scritta "Obiettivo" sopra: dove il titolo lo dice già, si
// lascia fuori
function ObjectiveField({ value, onChange, label, placeholder, disabled, caption = true }) {
	return (
		<Div
			d="flex"
			align="center"
			p="0.5rem 0.75rem"
			bg={theme.colors.gunmetal_lighter}
			rounded="15px"
			style={{ gap: "0.5rem" }}>
			<FontAwesomeIcon icon={faBullseye} color={theme.colors.gunmetal} />
			<Div flexGrow="1" style={{ minWidth: 0 }}>
				{caption && (
					<Text
						textSize="tiny"
						textWeight="bold"
						textColor={theme.colors.gunmetal}
						style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
						Obiettivo
					</Text>
				)}
				<input
					aria-label={label}
					value={value}
					placeholder={placeholder}
					disabled={disabled}
					maxLength={OBJECTIVE_MAX_TEXT}
					onChange={e => onChange(e.target.value)}
					style={{
						width: "100%",
						border: 0,
						padding: 0,
						background: "transparent",
						font: "inherit",
						color: theme.colors.text,
						outline: "none",
					}}
				/>
			</Div>
		</Div>
	)
}

// Un esercizio: cresce con quello che ci si scrive, e Invio non va a capo
// ma passa al prossimo, come in un elenco delle note
function ExerciseField({ value, label, inputRef, onChange, onEnter, onEmptyBackspace, disabled }) {
	const ref = useRef(null)
	useLayoutEffect(() => {
		const textarea = ref.current
		if (!textarea) return
		textarea.style.height = "auto"
		textarea.style.height = `${textarea.scrollHeight}px`
	}, [value])

	return (
		<textarea
			ref={element => {
				ref.current = element
				inputRef(element)
			}}
			rows={1}
			aria-label={label}
			value={value}
			disabled={disabled}
			maxLength={EXERCISE_MAX_TEXT}
			enterKeyHint="next"
			onChange={e => onChange(e.target.value.replace(/\n/g, " "))}
			onKeyDown={e => {
				if (e.key === "Enter" && !e.shiftKey) {
					e.preventDefault()
					onEnter()
				} else if (e.key === "Backspace" && value === "") {
					e.preventDefault()
					onEmptyBackspace()
				}
			}}
			style={{
				flex: 1,
				minWidth: 0,
				resize: "none",
				overflow: "hidden",
				// l'altezza si misura sul contenuto, padding compreso
				boxSizing: "border-box",
				border: 0,
				borderRadius: "15px",
				padding: "0.5rem 0.75rem",
				background: theme.colors.surface,
				font: "inherit",
				lineHeight: 1.4,
				color: theme.colors.text,
			}}
		/>
	)
}

// Un esempio per ogni obiettivo ancora vuoto: dice che cosa ci va, il
// perché della fase, e non l'esercizio
const OBJECTIVE_PLACEHOLDERS = {
	riscaldamento: "Es. ritmo e controllo della profondità",
	tecnica: "Es. impatto avanti, chiusura sopra la spalla",
	tattica: "Es. cambiare direzione al momento giusto",
	defaticamento: "Es. sciogliere, due parole su com'è andata",
}

// Il numero della fase, nel tondo
const PhaseNumber = ({ n }) => (
	<span
		aria-hidden="true"
		style={{
			width: "1.375rem",
			height: "1.375rem",
			borderRadius: "50%",
			background: theme.colors.gunmetal_lighter,
			color: theme.colors.gunmetal,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			fontSize: "0.75rem",
		}}>
		{n}
	</span>
)

// Il programma di una lezione da scrivere: l'obiettivo, il tipo di
// giocatore, su cosa si lavora e le quattro fasi, ognuna col suo obiettivo
// e i suoi esercizi. Lo usano il foglio della lezione e le lezioni del
// quaderno, che sono lo stesso programma.
//
// Ogni esercizio può venire dal quaderno e avere il suo schema: la sua
// provenienza (`sources`, src/utils/lessonContent.utils.js) e il suo
// disegno (`schemas`, src/utils/exerciseSchema.utils.js) lo seguono quando
// si aggiungono o si tolgono righe. Il tasto col campo, accanto a ogni
// esercizio, apre l'editor dello schema. Dove si scrive il programma di una lezione, accanto alle
// fasi e agli esercizi ci sono i tasti del quaderno: `phaseAction` sta
// accanto al nome della fase, `exerciseAction` accanto all'esercizio,
// prima della ✕, `exerciseNote` sotto.
function LessonContentFields({
	content,
	setContent,
	disabled,
	phaseAction,
	exerciseAction,
	exerciseNote,
}) {
	const recommended = recommendedFor(content.playerStyle)
	const hasRecommendations = recommended.strokes.size + recommended.situations.size > 0

	const update = changes => setContent(current => ({ ...current, ...changes }))

	// gli esercizi, le loro provenienze e i loro schemi cambiano insieme,
	// riga per riga
	const updateExercises = (phaseID, change) =>
		setContent(current => {
			const phase = current.phases[phaseID]
			const rows = change(
				phase.exercises.map((text, i) => ({
					text,
					source: phaseSources(phase)[i],
					schema: phaseSchemas(phase)[i],
				})),
			)
			return {
				...current,
				phases: {
					...current.phases,
					[phaseID]: {
						...phase,
						exercises: rows.map(row => row.text),
						sources: rows.map(row => row.source),
						schemas: rows.map(row => row.schema),
					},
				},
			}
		})

	// l'esercizio di cui si sta disegnando lo schema, `{ phaseID, index }`
	const [schemaTarget, setSchemaTarget] = useState(null)

	const setSchema = (phaseID, index, schema) =>
		updateExercises(phaseID, rows => rows.map((row, i) => (i === index ? { ...row, schema } : row)))

	// Dove mettere il cursore dopo aver aggiunto o tolto un esercizio: si
	// sa solo quando la riga c'è (o non c'è più), al giro dopo
	const exerciseRefs = useRef({})
	const [focusTarget, setFocusTarget] = useState(null)
	useEffect(() => {
		if (!focusTarget) return
		exerciseRefs.current[`${focusTarget.phaseID}-${focusTarget.index}`]?.focus()
		setFocusTarget(null)
	}, [focusTarget])

	const setObjective = (phaseID, objective) =>
		setContent(current => ({
			...current,
			phases: { ...current.phases, [phaseID]: { ...current.phases[phaseID], objective } },
		}))

	const setExercise = (phaseID, index, text) =>
		updateExercises(phaseID, rows => rows.map((row, i) => (i === index ? { ...row, text } : row)))

	// un esercizio nuovo subito dopo `index`, o in fondo
	const addExercise = (phaseID, index = content.phases[phaseID].exercises.length - 1) => {
		if (content.phases[phaseID].exercises.length >= MAX_EXERCISES) return
		updateExercises(phaseID, rows => [
			...rows.slice(0, index + 1),
			{ text: "", source: null, schema: null },
			...rows.slice(index + 1),
		])
		setFocusTarget({ phaseID, index: index + 1 })
	}

	const removeExercise = (phaseID, index, { focusPrevious = false } = {}) => {
		updateExercises(phaseID, rows => rows.filter((_, i) => i !== index))
		if (focusPrevious && index > 0) setFocusTarget({ phaseID, index: index - 1 })
	}

	return (
		<>
			<Paragraph title="Obiettivo della lezione:" m={{ b: "0.5rem" }}>
				<ObjectiveField
					label="Obiettivo della lezione"
					caption={false}
					value={content.objective}
					placeholder="Es. rovescio lungolinea anche sotto pressione"
					disabled={disabled}
					onChange={objective => update({ objective })}
				/>
			</Paragraph>

			<Paragraph title="Tipo di giocatore:" m={{ t: "1.25rem", b: "0.5rem" }}>
				{hasRecommendations && (
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.5rem" }}>
						Guida il tema: le voci consigliate hanno il pallino.
					</Text>
				)}
				<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }}>
					{PLAYER_STYLES.map(style => (
						<Chip
							key={style.id}
							text={style.label}
							color={theme.colors.primary}
							isSelected={content.playerStyle === style.id}
							disabled={disabled}
							// scelta singola: toccare quello scelto lo toglie
							onClick={() =>
								update({
									playerStyle: content.playerStyle === style.id ? null : style.id,
								})
							}
						/>
					))}
				</Div>
			</Paragraph>

			<Paragraph title="Su cosa si lavora:" m={{ t: "1.25rem", b: "0.5rem" }}>
				<FocusAreasPicker
					value={content}
					onChange={update}
					disabled={disabled}
					recommended={recommended}
				/>
			</Paragraph>

			<Paragraph title="Fasi della lezione:" m={{ t: "1.25rem" }}>
				<Text textSize="caption" textColor={theme.colors.text_light}>
					Per ogni fase l'obiettivo, il perché, e gli esercizi, cosa e come: uno per riga.
					Tutto facoltativo: prima come piano, dopo come diario.
				</Text>
				{PHASES.map((phase, phaseIndex) => {
					const { objective, exercises } = content.phases[phase.id]
					const schemas = phaseSchemas(content.phases[phase.id])
					const name = phase.label.toLowerCase()
					return (
						<Div
							key={phase.id}
							m={{ t: "1rem" }}
							d="flex"
							flexDir="column"
							style={{ gap: "0.5rem" }}>
							<Div d="flex" align="center" justify="space-between" style={{ gap: "0.5rem" }}>
								<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
									<PhaseNumber n={phaseIndex + 1} />
									<Text textWeight="600" textColor={theme.colors.gunmetal}>
										{phase.label}
									</Text>
								</Div>
								{phaseAction?.(phase)}
							</Div>
							<ObjectiveField
								label={`Obiettivo: ${name}`}
								value={objective}
								placeholder={OBJECTIVE_PLACEHOLDERS[phase.id]}
								disabled={disabled}
								onChange={text => setObjective(phase.id, text)}
							/>
							{exercises.map((exercise, index) => (
								<Div key={index} d="flex" flexDir="column" style={{ gap: "0.25rem" }}>
									<Div d="flex" align="flex-start" style={{ gap: "0.375rem" }}>
										<span
											aria-hidden="true"
											style={{
												flex: "none",
												width: "6px",
												height: "6px",
												borderRadius: "50%",
												background: theme.colors.gunmetal,
												margin: "1rem 0.25rem 0 0.125rem",
											}}
										/>
										<ExerciseField
											label={`Esercizio ${index + 1}: ${name}`}
											value={exercise}
											disabled={disabled}
											inputRef={element => {
												exerciseRefs.current[`${phase.id}-${index}`] = element
											}}
											onChange={text => setExercise(phase.id, index, text)}
											onEnter={() => addExercise(phase.id, index)}
											// una riga vuota se ne va, ma non se ha lo schema
											onEmptyBackspace={() =>
												!schemas[index] &&
												removeExercise(phase.id, index, { focusPrevious: true })
											}
										/>
										<Button
											h="2.25rem"
											w="2.25rem"
											p="0"
											bg="transparent"
											textColor={
												schemas[index] ? theme.colors.gunmetal : theme.colors.text_light
											}
											aria-label={`${schemas[index] ? "Modifica lo schema" : "Disegna lo schema"}: esercizio ${index + 1}, ${name}`}
											title="Schema"
											disabled={disabled}
											disableBg="transparent"
											onClick={() => setSchemaTarget({ phaseID: phase.id, index })}>
											<CourtIcon />
										</Button>
										{exerciseAction?.(phase, index)}
										<Button
											h="2.25rem"
											w="2.25rem"
											p="0"
											bg="transparent"
											aria-label={`Togli l'esercizio ${index + 1}: ${name}`}
											disabled={disabled}
											onClick={() => removeExercise(phase.id, index)}>
											<FontAwesomeIcon icon={faXmark} color={theme.colors.text_light} />
										</Button>
									</Div>
									{schemas[index] && (
										<ExerciseSchemaRow
											schema={schemas[index]}
											name={`esercizio ${index + 1}, ${name}`}
											disabled={disabled}
											onEdit={origin => setSchemaTarget({ phaseID: phase.id, index, origin })}
											onRemove={() => setSchema(phase.id, index, null)}
										/>
									)}
									{exerciseNote?.(phase, index)}
								</Div>
							))}
							{exercises.length < MAX_EXERCISES && (
								<Button
									w="100%"
									h="2.5rem"
									bg="transparent"
									rounded="15px"
									textColor={theme.colors.gunmetal}
									pos="relative"
									style={{ gap: "0.375rem" }}
									aria-label={`Aggiungi un esercizio: ${name}`}
									disabled={disabled}
									onClick={() => addExercise(phase.id)}>
									<DashedOutline color={theme.colors.gray_light} width={2} radius={15} />
									<FontAwesomeIcon icon={faPlus} size="sm" />
									<Text tag="span" textSize="caption" textWeight="600">
										Aggiungi esercizio
									</Text>
								</Button>
							)}
						</Div>
					)
				})}
			</Paragraph>

			{schemaTarget && (
				<SchemaEditor
					schema={phaseSchemas(content.phases[schemaTarget.phaseID])[schemaTarget.index]}
					title={content.phases[schemaTarget.phaseID].exercises[schemaTarget.index]}
					origin={schemaTarget.origin}
					onCancel={() => setSchemaTarget(null)}
					onDone={schema => {
						setSchema(schemaTarget.phaseID, schemaTarget.index, schema)
						setSchemaTarget(null)
					}}
				/>
			)}
		</>
	)
}

export default LessonContentFields
